import { useState } from "react";
import LearnLayout from "@/components/LearnLayout";
import { HOW_IT_WORKS_ROWS, HOW_IT_WORKS_FAQ } from "@/lib/howItWorksContent";
import { FeatureStack } from "@/components/learn/FeatureStack";
import { ShieldCheck, ChevronDown } from "lucide-react";

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid={`faq-toggle-${i}`}
      >
        <span className="font-display text-base font-semibold">{q}</span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-4 text-[1.06rem] text-foreground/75">{a}</p>}
    </div>
  );
}

function HowItAllWorksBody() {
  const steps = [
    { n: "01", title: "Create your account", desc: "Join SRC: create a simple account to help us moderate and keep the community genuine and spam-free." },
    { n: "02", title: "Set up your profile", desc: "Link SimHub and edit your sim racer profile to build your brand." },
    { n: "03", title: "Race, trade & connect", desc: "Buy and sell safely, launch your sim racing career, shop, discuss, and share laps and achievements to grow your brand." },
  ];
  return (
    <div>
      <p className="text-[1.06rem] leading-relaxed text-foreground/75">
        Buy, sell, compare new prices on gear, and find sponsors all in one place. Bring in your telemetry, experience, and achievements to launch your sim racing career and build your name.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {steps.map((s) => (
          <div key={s.n} className="rounded-lg border border-border/60 bg-background/40 p-4">
            <span className="font-display text-3xl font-bold text-primary/70">{s.n}</span>
            <h4 className="mt-1 font-display text-base font-semibold text-foreground">{s.title}</h4>
            <p className="mt-1.5 text-[1.06rem] leading-relaxed text-foreground/75">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LearnHowItWorks() {
  return (
    <LearnLayout
      testId="learn-how-it-works"
      kicker=""
      title="One Platform, All the tools you need"
      sub="Everything sim racers need - trading, shopping, community, sponsorship, and telemetry - wired together on web and mobile so it works for you with no extra effort."
      heroImage="https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/u7agxbq4_SRC%20Web%20FAQ%20Banner.webp"
      heroBgPosition="60% center"
      heroImageTranslateX="16%"
      heroImageOpacity={0.92}
      heroOverlayColor="rgba(0,0,0,0.18)"
      heroFadeGradient="linear-gradient(to right, hsl(240 8% 4%) 0%, hsl(240 8% 4% / 0.5) 45%, hsl(240 8% 4% / 0.12) 100%)"
      bodyBg="#eceef1"
    >
      {/* Feature deep-dives */}
      <section className="pl-12 sm:pl-14">
        <div className="relative z-20 inline-block -skew-x-[11deg] bg-primary px-4 py-2 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.65)] sm:px-5 sm:py-2.5">
          <h2 className="skew-x-[11deg] font-display text-[1.94rem] font-bold italic uppercase leading-none text-white sm:text-[2.43rem]">
            Every piece of the platform
          </h2>
        </div>
        <div className="relative z-10 -mt-3">
          <FeatureStack
            testId="how-it-works-feature-stack"
            tabBadge
            largeBody
            items={[
              {
                pos: "01",
                title: "How it all works",
                summary: "Create your account, set up your profile, then race, trade and connect.",
                content: <HowItAllWorksBody />,
              },
              ...HOW_IT_WORKS_ROWS.map((row, idx) => {
                const Icon = row.icon;
                return {
                  pos: String(idx + 2).padStart(2, "0"),
                  title: row.title,
                  summary: row.desc,
                  content: (
                    <div>
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary/15">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <p className="text-[1.06rem] leading-relaxed text-foreground/75">{row.desc}</p>
                      </div>
                      <ul className="mt-4 grid gap-1.5 sm:grid-cols-2">
                        {row.points.map((p) => (
                          <li key={p} className="flex items-center gap-2 rounded-md border border-border/60 bg-background/40 px-2.5 py-1.5 text-[1.06rem]">
                            <span className="flex flex-shrink-0 items-center gap-0.5" aria-hidden>
                              <span className="h-1.5 w-2.5 rounded-[1px] bg-emerald-400/80" />
                              <span className="h-1.5 w-2.5 rounded-[1px] bg-violet-400/80" />
                            </span>
                            <span className="text-foreground/75">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                };
              }),
            ]}
          />
        </div>
      </section>

      {/* Clarifications strip — full-width carbon band */}
      <section
        className="relative mt-20 overflow-hidden bg-carbon"
        style={{ width: "100vw", marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
      >
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase text-white">
            <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
            Clarifications
          </h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              "Web app and mobile app share one account and one dataset — start on desktop, finish on mobile.",
              "New accounts need a quick super-admin approval (usually under 24 hours).",
              "Marketplace payments are held securely; funds release only once the buyer confirms delivery.",
              "SimHub live telemetry is a mobile-only feature; recorded stats/history sync everywhere.",
              "iRacing integration is optional — you can use SRC fully without linking it.",
              "You can sell locally or ship; when shipping, SRC boxes and carrier discounts are optional but recommended.",
            ].map((c) => (
              <li key={c} className="flex items-start gap-2 text-[1.06rem]">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                <span className="text-foreground/75">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-20">
        <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase" style={{ color: "#0b0b0d" }}>
          <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
          Frequently asked
        </h2>
        <div className="mt-8 space-y-3" data-testid="faq-list">
          {HOW_IT_WORKS_FAQ.map((f, i) => <FaqItem key={f.q} i={i} q={f.q} a={f.a} />)}
        </div>
      </section>
    </LearnLayout>
  );
}
